import { Menu as MenuIcon, Plus, Trash2 } from "lucide-react";
import { inputCls, labelCls, MENU_ICON_OPTIONS } from "./constants";

/**
 * Górne menu: widoczność każdej zakładki + edytowalne etykiety + niestandardowe podstrony.
 */
export default function MenuSection({ brand, setBrand }) {
  const menu = brand.menu || {};
  const labels = menu.labels || {};
  const customPages = Array.isArray(menu.custom_pages) ? menu.custom_pages : [];

  const setMenu = (patch) => setBrand({ ...brand, menu: { ...menu, ...patch } });
  const setLabel = (key, value) => setMenu({ labels: { ...labels, [key]: value } });
  const setToggle = (key, value) => setMenu({ [key]: value });

  const addCustom = () =>
    setMenu({ custom_pages: [...customPages, { label: "Nowa zakładka", url: "/", icon: "star" }] });
  const updateCustom = (idx, field, value) =>
    setMenu({ custom_pages: customPages.map((p, i) => (i === idx ? { ...p, [field]: value } : p)) });
  const removeCustom = (idx) =>
    setMenu({ custom_pages: customPages.filter((_, i) => i !== idx) });

  const items = [
    { key: "show_home", labelKey: "home", label: "Start (strona główna)" },
    { key: "show_plans", labelKey: "plans", label: "Abonamenty" },
    { key: "show_internet", labelKey: "internet", label: "Internet mobilny" },
    { key: "show_order_sim", labelKey: "order_sim", label: "Zamów SIM" },
    { key: "show_port_number", labelKey: "port_number", label: "Przenieś numer" },
    { key: "show_contact", labelKey: "contact", label: "Kontakt" },
  ];

  return (
    <section>
      <h3 className="flex items-center gap-2 font-bold text-slate-800 text-lg mb-4">
        <MenuIcon size={18} className="text-[#1f3a8a]" /> Górne menu — widoczność, nazwy i podstrony
      </h3>
      <div className="rounded-xl border border-slate-200 p-5 bg-slate-50/50 space-y-5">
        {/* Toggles + labels dla wbudowanych */}
        <div className="space-y-2">
          <div className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Wbudowane zakładki</div>
          {items.map((it) => {
            const key = it.key;
            const checked = it.key === "show_contact" ? !!menu[key] : menu[key] !== false;
            return (
              <div key={key} className="grid sm:grid-cols-[auto,1fr] gap-3 items-center p-3 bg-white rounded-lg border border-slate-200">
                <label className="flex items-center gap-2 min-w-[220px]">
                  <input
                    data-testid={`menu-toggle-${it.labelKey}`}
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => setToggle(key, e.target.checked)}
                    className="accent-[#1f3a8a] w-5 h-5"
                  />
                  <span className="text-sm font-semibold text-slate-800">{it.label}</span>
                </label>
                <input
                  data-testid={`menu-label-${it.labelKey}`}
                  className={inputCls}
                  value={labels[it.labelKey] || ""}
                  onChange={(e) => setLabel(it.labelKey, e.target.value)}
                  placeholder={it.label}
                />
              </div>
            );
          })}
        </div>

        {/* CTA button label + link + toggle */}
        <div className="p-3 bg-white rounded-lg border border-slate-200 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">Przycisk CTA „Zamów teraz" (czerwony, w prawym górnym rogu)</div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                data-testid="menu-cta-enabled"
                checked={menu.cta_enabled !== false}
                onChange={(e) => setBrand({ ...brand, menu: { ...menu, cta_enabled: e.target.checked } })}
                className="w-4 h-4"
              />
              <span className="font-semibold">Pokazuj</span>
            </label>
          </div>
          <div className={`grid md:grid-cols-2 gap-3 ${menu.cta_enabled === false ? "opacity-40 pointer-events-none" : ""}`}>
            <div>
              <label className={labelCls}>Tekst przycisku</label>
              <input
                data-testid="menu-label-cta"
                className={inputCls}
                value={labels.cta || ""}
                onChange={(e) => setLabel("cta", e.target.value)}
                placeholder="Zamów teraz"
              />
            </div>
            <div>
              <label className={labelCls}>Dokąd przekierowuje</label>
              <select
                data-testid="menu-cta-url"
                className={inputCls}
                value={menu.cta_url || "/zamow-sim"}
                onChange={(e) => setBrand({ ...brand, menu: { ...menu, cta_url: e.target.value } })}
              >
                <option value="/zamow-sim">/zamow-sim — Formularz zamówienia SIM</option>
                <option value="/przenies-numer">/przenies-numer — Przenieś numer</option>
                <option value="/abonamenty">/abonamenty — Abonamenty</option>
                <option value="/internet-mobilny">/internet-mobilny — Internet mobilny</option>
                <option value="/kontakt">/kontakt — Kontakt</option>
                <option value="/regulamin">/regulamin — Regulamin</option>
                <option value="__custom__">Inny link (podaj poniżej)</option>
              </select>
              {!["/zamow-sim","/przenies-numer","/abonamenty","/internet-mobilny","/kontakt","/regulamin"].includes(menu.cta_url || "/zamow-sim") && (
                <input
                  data-testid="menu-cta-url-custom"
                  className={`${inputCls} mt-2`}
                  value={menu.cta_url === "__custom__" ? "" : (menu.cta_url || "")}
                  onChange={(e) => setBrand({ ...brand, menu: { ...menu, cta_url: e.target.value } })}
                  placeholder="/moja-strona lub https://..."
                />
              )}
            </div>
          </div>
        </div>

        {/* Custom pages */}
        <div className="border-t border-slate-200 pt-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-bold text-slate-600 uppercase tracking-wider">Niestandardowe zakładki</div>
              <div className="text-xs text-slate-500 mt-1">Dodaj własną zakładkę do menu (np. „Promocje", „Blog"). URL może być wewnętrzny („/blog") lub zewnętrzny („https://...").</div>
            </div>
            <button
              type="button"
              data-testid="menu-add-custom"
              onClick={addCustom}
              className="inline-flex items-center gap-1.5 bg-[#1f3a8a] hover:bg-[#15296b] text-white font-semibold text-sm px-3 py-2 rounded-full transition"
            >
              <Plus size={14} /> Dodaj zakładkę
            </button>
          </div>
          {customPages.length === 0 && (
            <div className="text-sm text-slate-400 italic p-3">Brak niestandardowych zakładek.</div>
          )}
          <div className="space-y-2">
            {customPages.map((p, i) => (
              <div key={i} data-testid={`menu-custom-${i}`} className="grid sm:grid-cols-[1fr,1fr,180px,auto] gap-2 items-center p-3 bg-white rounded-lg border border-slate-200">
                <input
                  data-testid={`menu-custom-label-${i}`}
                  className={inputCls}
                  value={p.label || ""}
                  onChange={(e) => updateCustom(i, "label", e.target.value)}
                  placeholder="Nazwa zakładki"
                />
                <input
                  data-testid={`menu-custom-url-${i}`}
                  className={inputCls}
                  value={p.url || ""}
                  onChange={(e) => updateCustom(i, "url", e.target.value)}
                  placeholder="/promocje lub https://..."
                />
                <select
                  data-testid={`menu-custom-icon-${i}`}
                  className={inputCls}
                  value={p.icon || "star"}
                  onChange={(e) => updateCustom(i, "icon", e.target.value)}
                >
                  {MENU_ICON_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  data-testid={`menu-custom-remove-${i}`}
                  onClick={() => removeCustom(i)}
                  className="text-red-500 hover:text-red-700 p-2"
                  title="Usuń"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
